import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { deleteKnowledgeGraph, updateKnowledgeGraph } from "@/api/knowledgeGraphApi";
import type { KnowledgeGraph } from "@/types/knowledgeGraph";

interface UseKgGraphMutationsOptions {
	selectedGraphId: string | null;
	useMock: boolean;
	graphs?: KnowledgeGraph[];
	onDeleted: () => void;
}

export function useKgGraphMutations({ selectedGraphId, useMock, graphs, onDeleted }: UseKgGraphMutationsOptions) {
	const queryClient = useQueryClient();

	const updateMutation = useMutation({
		mutationFn: ({ id, name }: { id: string; name: string }) => updateKnowledgeGraph(id, { name }),
		onSuccess: (_, { id }) => {
			queryClient.invalidateQueries({ queryKey: ["knowledgeGraphs"] });
			queryClient.invalidateQueries({ queryKey: ["knowledgeGraphVisualization", id] });
			queryClient.invalidateQueries({ queryKey: ["workspace"] });
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteKnowledgeGraph(id),
		onSuccess: (_, deletedId) => {
			queryClient.invalidateQueries({ queryKey: ["knowledgeGraphs"] });
			queryClient.invalidateQueries({ queryKey: ["knowledgeGraphVisualization", deletedId] });
			queryClient.invalidateQueries({ queryKey: ["workspace"] });
			onDeleted();
		},
	});

	const handleRename = useCallback(() => {
		if (!selectedGraphId || useMock) return;
		const currentName = graphs?.find((g) => g.id === selectedGraphId)?.name ?? "";
		const newName = window.prompt("Rename knowledge graph", currentName);
		if (newName != null && newName.trim() !== "" && newName !== currentName) {
			updateMutation.mutate({ id: selectedGraphId, name: newName.trim() });
		}
	}, [selectedGraphId, useMock, graphs, updateMutation]);

	const handleDelete = useCallback(() => {
		if (!selectedGraphId || useMock) return;
		const currentName = graphs?.find((g) => g.id === selectedGraphId)?.name ?? "";
		if (window.confirm(`Delete "${currentName}"? This cannot be undone.`)) {
			deleteMutation.mutate(selectedGraphId);
		}
	}, [selectedGraphId, useMock, graphs, deleteMutation]);

	return { updateMutation, deleteMutation, handleRename, handleDelete };
}
